import type { BridgeHandlerName } from './types'

/**
 * 런타임 타입 검증 에러
 * Flutter가 예상과 다른 형태의 데이터를 반환했을 때 발생
 * 재시도해도 동일한 결과이므로 즉시 실패 처리해야 함
 */
export class BridgeValidationError extends Error {
  constructor(handler: string, message: string) {
    super(`[Bridge] "${handler}" validation failed: ${message}`)
    this.name = 'BridgeValidationError'
  }
}

/**
 * 핸들러별 응답 필드 검증 규칙
 * Flutter가 반환하는 값이 TypeScript 타입과 일치하는지 런타임에서 확인
 * as 캐스팅만으로는 잡을 수 없는 타입 불일치를 여기서 잡음
 *
 * 반환값: null이면 통과, 문자열이면 에러 메시지
 */
const RESPONSE_VALIDATORS: Partial<Record<BridgeHandlerName, (result: unknown) => string | null>> = {
  healthCheck: (result) => {
    if (typeof result !== 'object' || result === null || !('status' in result) || !('timestamp' in result)) {
      return 'expected { status: string, timestamp: string }'
    }
    return null
  },
  getToken: (result) => {
    if (typeof result !== 'object' || result === null || !('token' in result)) {
      return 'expected { token: string, bottom?: string }'
    }
    const token = (result as { token: unknown }).token
    if (typeof token !== 'string' || token.length === 0) {
      return 'token must be a non-empty string'
    }
    return null
  },
  getUserInfo: (result) => {
    if (typeof result !== 'object' || result === null || !('userId' in result) || !('nickname' in result)) {
      return 'expected { userId: string, nickname: string }'
    }
    return null
  },
  shareLink: (result) => {
    if (typeof result !== 'object' || result === null || !('shared' in result)) {
      return 'expected { shared: boolean }'
    }
    return null
  },
  openBottom: (result) => {
    if (typeof result !== 'object' || result === null || !('type' in result)) {
      return 'expected { type: "share" | "order" | "alarm" | "quiz" }'
    }
    const type = (result as { type: unknown }).type
    if (type !== 'share' && type !== 'order' && type !== 'alarm' && type !== 'quiz') {
      return 'type must be one of "share" | "order" | "alarm" | "quiz"'
    }
    return null
  },
}

/**
 * Flutter 응답을 핸들러 타입에 맞게 검증
 * void 응답(undefined, null)은 호출 전에 스킵해야 함
 */
export function validateResponse(name: BridgeHandlerName, result: unknown) {
  const validator = RESPONSE_VALIDATORS[name]
  if (!validator) return

  const error = validator(result)
  if (error) {
    throw new BridgeValidationError(name, `${error}, got ${JSON.stringify(result)}`)
  }
}
